import { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import { TransferUnderpayment } from 'services/paymentHandlers/types';

type TransferContextProps = {
  transactionId: string;
  emailAddress: string | null;
  phone: string | null;
  amount: number;
  virtualAccountNumber: string;
  virtualAccountName: string;
  bankCode: string;
  currency: string;
  /** Minutes; undefined when the provider sets no expiry. */
  validityTime?: number;
  /** The provider's own reference for status lookups, when it has one. */
  reference: string;
  /** Set once the customer's transfer came up short; undefined until then. */
  underpayment?: TransferUnderpayment;
  changeTransferDetails: (details: Partial<TransferContextProps>) => void;
};

export const TransferContext = createContext<TransferContextProps>({
  transactionId: '',
  emailAddress: null,
  phone: null,
  amount: 0,
  virtualAccountNumber: '',
  virtualAccountName: '',
  bankCode: '',
  currency: '',
  validityTime: 0,
  reference: '',
  changeTransferDetails: () => {},
});

const TransferProvider = ({ children }: { children: ReactNode }) => {
  const [transferDetails, setTransferDetails] = useState<TransferContextProps>({
    transactionId: '',
    emailAddress: null,
    phone: null,
    amount: 0,
    virtualAccountNumber: '',
    virtualAccountName: '',
    bankCode: '',
    currency: '',
    validityTime: 0,
    reference: '',
    changeTransferDetails: () => {},
  });

  const changeTransferDetails = useCallback(
    (details: Partial<TransferContextProps>) => {
      setTransferDetails((prevDetails) => ({
        ...prevDetails,
        ...details,
      }));
    },
    [setTransferDetails]
  );

  const contextValue = useMemo(
    () => ({ ...transferDetails, changeTransferDetails }),
    [transferDetails, changeTransferDetails]
  );

  return (
    <TransferContext.Provider value={contextValue}>
      {children}
    </TransferContext.Provider>
  );
};

export default TransferProvider;
